import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
  ShieldCheck, LockKey, FileLock, GitCommit, KeyReturn, EyeSlash,
  CheckCircle, ArrowRight, Cube, Globe, Database, Receipt
} from '@phosphor-icons/react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_moneyswap-9/artifacts/pvoha9c5_D%24.png";

const PILLARS = [
  {
    icon: ShieldCheck,
    title: 'SOC 2 Type II — In Progress',
    badge: 'AUDIT WINDOW OPEN',
    body:
      "We're operating under the SOC 2 Trust Services Criteria — Security, Availability, Processing Integrity, Confidentiality, and Privacy. The technical controls below are live in production today; the formal Type II observation window is underway.",
    bullets: [
      'Role-based access with least-privilege defaults',
      'Encrypted data at rest and in transit (TLS 1.2+)',
      'Quarterly access reviews and incident-response drills',
      'Vendor sub-processor inventory maintained',
    ],
    testId: 'pillar-soc2',
  },
  {
    icon: FileLock,
    title: 'Field-Level Encryption',
    badge: 'AES-128-CBC + HMAC',
    body:
      'Sensitive fields — phone numbers, OAuth refresh tokens, and other PII — are encrypted before they touch the database using authenticated symmetric encryption (Fernet). Keys live in environment-managed secrets, never in code, and rotate without downtime via a MultiFernet chain.',
    bullets: [
      'Every ciphertext is tagged (fernet:v1) for forward compatibility',
      'Key rotation supports a window of previous keys for zero-downtime cutover',
      'Decryption errors are alerted, not silenced',
      'No plaintext PII mirrors are written alongside ciphertext',
    ],
    testId: 'pillar-encryption',
  },
  {
    icon: GitCommit,
    title: 'Tamper-Evident Audit Log',
    badge: 'CRYPTOGRAPHIC CHAIN',
    body:
      "Every privileged action — logins, OAuth handshakes, settings changes, money movement — is appended to a hash-chained log. Each entry stores the SHA-256 of the previous entry's canonical JSON, so any insertion, edit, or deletion breaks the chain and is detectable by an admin in a single API call.",
    bullets: [
      'Genesis-anchored chain with deterministic JSON hashing',
      'Per-entry actor, request IP, user-agent, and target',
      'Settings updates record a redacted before/after diff',
      "Admin endpoint pinpoints the exact 'seq' of any tampered entry",
    ],
    testId: 'pillar-audit',
  },
];

const CONTROL_GRID = [
  { icon: LockKey, label: 'Header-only sessions', desc: 'JWTs in Authorization / X-Access-Token — no cross-site cookies, CORS allow_credentials=false.' },
  { icon: KeyReturn, label: 'Anti-CSRF on OAuth', desc: 'Single-use 32-byte state tokens with a 10-minute TTL; consumed atomically.' },
  { icon: EyeSlash, label: 'Strict input schemas', desc: 'Pydantic v2 with extra=forbid on settings endpoints — unknown fields are rejected.' },
  { icon: Cube, label: 'Hardened response headers', desc: 'HSTS, X-Frame-Options DENY, Referrer-Policy, Permissions-Policy, COEP, CORP.' },
  { icon: Database, label: 'Brute-force lockouts', desc: 'Per-identifier failed-attempt counter with 15-minute cooldowns.' },
  { icon: Receipt, label: 'PCI scope minimisation', desc: 'Payments tokenised by Stripe & PayPal — no card data ever touches our servers.' },
];

const TIMELINE = [
  { quarter: 'Q4 2025', label: 'Security baseline', items: ['Bcrypt hashing', 'JWT + bearer headers', 'Role-based admin'] },
  { quarter: 'Q1 2026', label: 'SOC 2 technical controls', items: ['Field-level encryption', 'Chained audit log', 'Strict input schemas', 'Hardened headers'] },
  { quarter: 'Q2 2026', label: 'Audit & attestation', items: ['Type II observation window', 'Penetration test', 'Vendor risk review'] },
  { quarter: 'Q3 2026', label: 'Beyond SOC 2', items: ['Field-encrypted KYC vault', 'Tenant-scoped HSM keys', 'Continuous compliance monitoring'] },
];

const FAQ = [
  {
    q: 'Are you SOC 2 certified today?',
    a: "Not yet — Type II is an attestation issued after a 3-to-12-month observation of controls in operation. We're inside that window. The technical controls (encryption, chained audit, strict auth) are live now and you can verify the public ones via the API.",
  },
  {
    q: "What happens to my data if you get breached?",
    a: 'Sensitive fields like phone numbers and OAuth tokens are field-encrypted with keys held outside the database tier. A database-only breach would not expose plaintext PII. Payment card data is never stored — Stripe and PayPal handle it under their own PCI scope.',
  },
  {
    q: "Who can read the audit log?",
    a: 'Only platform admins, via authenticated API. The chain itself is tamper-evident: any modification breaks the hash linkage and our /audit/chain/verify endpoint will pinpoint the exact entry that was touched.',
  },
  {
    q: 'How do you handle data deletion under GDPR?',
    a: 'You can request a full export or deletion of your account from Settings → Privacy. We honour erasure within 30 days, retaining only what we are legally required to (transaction records for tax/AML).',
  },
];

export default function TrustSecurityPage() {
  const { isAuthenticated } = useAuth();

  return (
    <div className="min-h-screen bg-[#111111]" data-testid="trust-security-page">
      {/* Header */}
      <header className="bg-[#111111]/80 backdrop-blur-xl border-b border-[#222222] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="trust-logo-link">
            <img src={LOGO_URL} alt="Divispend" className="h-8 sm:h-10 w-auto" />
          </Link>
          <nav className="flex items-center gap-3">
            {isAuthenticated ? (
              <Link to="/dashboard">
                <Button className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm" data-testid="trust-dashboard-button">
                  Dashboard <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="text-[#999999] hover:text-white hover:bg-[#333333] rounded-sm" data-testid="trust-login-button">
                    Sign in
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm" data-testid="trust-register-button">
                    Get Started
                  </Button>
                </Link>
              </>
            )}
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 sm:py-24 border-b border-[#222222]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-[#22C55E]/10 border border-[#22C55E]/30 rounded-full px-4 py-1.5 mb-6" data-testid="trust-hero-badge">
            <ShieldCheck className="w-4 h-4 text-[#22C55E]" weight="duotone" />
            <span className="text-[11px] tracking-[0.18em] uppercase font-bold text-[#22C55E]">Trust &amp; Security</span>
          </div>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-5"
            style={{ fontFamily: 'Cabinet Grotesk' }}
          >
            Money moves. We move it carefully.
          </h1>
          <p className="text-[#999999] max-w-2xl mx-auto text-base leading-relaxed">
            Divispend is built to enterprise-grade standards from day one — encrypted PII, a tamper-evident
            audit chain, and a SOC 2 Type II observation window already in progress. Here&apos;s what that means
            in plain English.
          </p>
        </div>
      </section>

      {/* Three pillars */}
      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 space-y-6">
          {PILLARS.map((p) => (
            <div
              key={p.title}
              data-testid={p.testId}
              className="bg-[#1A1A1A] border border-[#222222] hover:border-[#22C55E]/30 transition-colors rounded-sm p-6 sm:p-8 grid grid-cols-1 md:grid-cols-[64px_1fr] gap-5"
            >
              <div className="w-12 h-12 rounded-sm bg-[#22C55E]/10 border border-[#22C55E]/30 flex items-center justify-center shrink-0">
                <p.icon className="w-6 h-6 text-[#22C55E]" weight="duotone" />
              </div>
              <div>
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <h2
                    className="text-xl sm:text-2xl font-bold text-white"
                    style={{ fontFamily: 'Cabinet Grotesk' }}
                  >
                    {p.title}
                  </h2>
                  <span className="text-[10px] tracking-[0.18em] uppercase font-bold text-[#22C55E] bg-[#22C55E]/10 border border-[#22C55E]/30 rounded-full px-2.5 py-0.5">
                    {p.badge}
                  </span>
                </div>
                <p className="text-[#BBBBBB] text-sm leading-relaxed mb-4">{p.body}</p>
                <ul className="space-y-2">
                  {p.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2.5 text-[#999999] text-sm">
                      <CheckCircle className="w-4 h-4 text-[#22C55E] shrink-0 mt-0.5" weight="fill" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Control grid */}
      <section className="py-16 bg-[#0A0A0A] border-y border-[#222222]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#22C55E] mb-3 text-center">
            Defence in depth
          </p>
          <h2
            className="text-3xl sm:text-4xl font-bold tracking-tight text-white text-center mb-12"
            style={{ fontFamily: 'Cabinet Grotesk' }}
          >
            Controls running on every request
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {CONTROL_GRID.map((c) => (
              <div
                key={c.label}
                data-testid={`control-${c.label.toLowerCase().replace(/\s+/g, '-')}`}
                className="bg-[#111111] border border-[#222222] rounded-sm p-5 hover:border-[#22C55E]/30 transition-colors"
              >
                <c.icon className="w-6 h-6 text-[#22C55E] mb-3" weight="duotone" />
                <div className="text-white font-bold text-sm mb-1" style={{ fontFamily: 'Cabinet Grotesk' }}>
                  {c.label}
                </div>
                <div className="text-[#999999] text-xs leading-relaxed">{c.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Roadmap timeline */}
      <section className="py-16 sm:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#22C55E] mb-3 text-center">
            Compliance roadmap
          </p>
          <h2
            className="text-3xl sm:text-4xl font-bold tracking-tight text-white text-center mb-12"
            style={{ fontFamily: 'Cabinet Grotesk' }}
          >
            Where we are, where we&apos;re going
          </h2>
          <div className="relative">
            <div className="absolute left-4 sm:left-1/2 top-0 bottom-0 w-px bg-[#222222]" aria-hidden />
            <div className="space-y-8">
              {TIMELINE.map((t, idx) => (
                <div
                  key={t.quarter}
                  data-testid={`timeline-${t.quarter.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`relative grid grid-cols-[32px_1fr] sm:grid-cols-2 gap-4 sm:gap-12 ${
                    idx % 2 === 0 ? 'sm:[&>div:nth-child(2)]:order-1' : ''
                  }`}
                >
                  <div className="absolute left-2 sm:left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-[#22C55E] border-4 border-[#111111]" />
                  <div className={idx % 2 === 0 ? 'sm:text-right sm:pr-12' : 'sm:pl-12'}>
                    <div className="text-[#22C55E] text-xs tracking-[0.18em] uppercase font-bold mb-1 pl-10 sm:pl-0">
                      {t.quarter}
                    </div>
                    <div
                      className="text-white text-lg font-bold pl-10 sm:pl-0"
                      style={{ fontFamily: 'Cabinet Grotesk' }}
                    >
                      {t.label}
                    </div>
                  </div>
                  <div className={idx % 2 === 0 ? 'sm:pl-12' : 'sm:text-right sm:pr-12'}>
                    <ul className={`space-y-1.5 pl-10 sm:pl-0 ${idx % 2 === 0 ? '' : 'sm:[&_li]:justify-end'}`}>
                      {t.items.map((item) => (
                        <li
                          key={item}
                          className={`flex items-center gap-2 text-sm text-[#BBBBBB] ${
                            idx % 2 === 0 ? '' : 'sm:flex-row-reverse'
                          }`}
                        >
                          <CheckCircle
                            className={`w-3.5 h-3.5 shrink-0 ${
                              idx <= 1 ? 'text-[#22C55E]' : 'text-[#555555]'
                            }`}
                            weight={idx <= 1 ? 'fill' : 'regular'}
                          />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-[#0A0A0A] border-t border-[#222222]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#22C55E] mb-3 text-center">
            Frequently asked
          </p>
          <h2
            className="text-3xl sm:text-4xl font-bold tracking-tight text-white text-center mb-10"
            style={{ fontFamily: 'Cabinet Grotesk' }}
          >
            Straight answers
          </h2>
          <div className="space-y-3">
            {FAQ.map((item, i) => (
              <details
                key={item.q}
                data-testid={`trust-faq-${i}`}
                className="group bg-[#111111] border border-[#222222] rounded-sm open:border-[#22C55E]/30"
              >
                <summary className="cursor-pointer list-none flex items-start justify-between gap-4 p-5">
                  <span
                    className="text-white font-semibold text-sm sm:text-base"
                    style={{ fontFamily: 'Cabinet Grotesk' }}
                  >
                    {item.q}
                  </span>
                  <span className="text-[#22C55E] text-xl leading-none transition-transform group-open:rotate-45">
                    +
                  </span>
                </summary>
                <p className="px-5 pb-5 text-[#BBBBBB] text-sm leading-relaxed">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <Globe className="w-10 h-10 text-[#22C55E] mx-auto mb-4" weight="duotone" />
          <h2
            className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white mb-4"
            style={{ fontFamily: 'Cabinet Grotesk' }}
          >
            Have a security question we didn&apos;t answer?
          </h2>
          <p className="text-[#999999] mb-8 max-w-xl mx-auto text-sm sm:text-base">
            We respond to every security disclosure within one business day. Reach our team and we&apos;ll
            share our latest controls inventory under NDA.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <a href="mailto:security@divispend.com">
              <Button
                className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm h-12 px-7 text-base font-semibold"
                data-testid="trust-contact-security"
              >
                Email security@divispend.com
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </a>
            <Link to="/privacy_policy">
              <Button
                variant="ghost"
                className="text-[#999999] hover:text-white hover:bg-[#1A1A1A] rounded-sm h-12 px-6"
                data-testid="trust-privacy-link"
              >
                Read our privacy policy
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#222222] py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <img src={LOGO_URL} alt="Divispend" className="h-8 w-auto" />
          <div className="flex items-center gap-6">
            <Link to="/features" className="text-sm text-[#999999] hover:text-white transition-colors" data-testid="trust-footer-features-link">
              Features
            </Link>
            <Link to="/trust" className="text-sm text-[#999999] hover:text-white transition-colors" data-testid="trust-footer-trust-link">
              Trust &amp; Security
            </Link>
            <Link to="/privacy_policy" className="text-sm text-[#999999] hover:text-white transition-colors" data-testid="trust-footer-privacy-link">
              Privacy Policy
            </Link>
            <p className="text-sm text-[#999999]">&copy; 2026 Divispend. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
